/**
 * Pill — a fully rounded label chip for a value or status: a pipeline stage, a
 * lifecycle state, a category.
 *
 * Shares the chip scale with `Badge` (see `chip-styles.ts`), so a Pill and a
 * Badge of the same `size` are the same height, type and outline. Use `Badge`
 * for the badge palette and its `BadgeWith*` variants; use `Pill` when the
 * chip's colour is data — a user-configured stage colour on the dot, or the
 * whole chip tinted from it — or when it should sit quietly in the neutral tone.
 *
 * Interactive via `onClick`, with the same span-with-role semantics as `Badge`
 * (`useActivatable`), so it stays valid inside flow content. When it has to be
 * a real element of another kind (a react-aria `Button` opening a menu, a router
 * `Link`), render that element with `pillClassName()`, `pillTintStyle()` and
 * `<PillDot>` instead of forking the styles.
 */

import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Dot } from '@/lib/foundations/dot-icon';
import { cx } from '@/utils/cx';

import type { Sizes } from './badge-types';
import { CHIP_DISABLED_CLASSES, CHIP_INTERACTIVE_CLASSES } from './chip-styles';
import {
  pillClassName,
  type PillColor,
  pillColorStyles,
  pillTintStyle,
} from './pill-styles';
import { useActivatable } from './useActivatable';

export interface PillDotProps {
  /** The pill's colour — supplies the dot's tone when `dotColor` is absent. */
  color?: PillColor;
  /**
   * Any CSS colour for the dot. For colours that are data rather than design
   * tokens, such as a pipeline stage's configured hex.
   */
  dotColor?: string;
}

/** The leading status dot, sized and toned for a Pill. */
export function PillDot({ color = 'neutral', dotColor }: PillDotProps) {
  // A user-configured colour cannot be a Tailwind class (arbitrary values are
  // compiled, not templated), so this is the one sanctioned inline style —
  // isolated in this primitive rather than repeated at every call site.
  const style = useMemo(
    () => (dotColor ? { color: dotColor } : undefined),
    [dotColor]
  );
  return (
    <Dot
      size='sm'
      aria-hidden='true'
      className={cx('shrink-0', !dotColor && pillColorStyles(color).addon)}
      style={style}
    />
  );
}

export interface PillProps {
  size?: Sizes;
  color?: PillColor;
  /**
   * Tint the whole pill — background, ring and text — from any CSS colour, such
   * as a pipeline stage's hex. Replaces `color`.
   */
  tint?: string;
  /**
   * Leading dot. `true` uses the tint when set, otherwise the colour's own dot
   * tone; a string is an explicit CSS colour for the dot.
   */
  dot?: boolean | string;
  children: ReactNode;
  className?: string;
  /** Makes the pill activatable (role="button", Enter/Space, stop-propagated). */
  onClick?: () => void;
  /** Accessible name when the visible text isn't a sufficient label. */
  'aria-label'?: string;
  /** Blocks activation and dims an interactive pill. */
  isDisabled?: boolean;
  /** Native tooltip — commonly the reason an interactive pill is disabled. */
  title?: string;
}

export function Pill({
  size = 'md',
  color = 'neutral',
  tint,
  dot = false,
  children,
  className,
  onClick,
  'aria-label': ariaLabel,
  isDisabled = false,
  title,
}: PillProps) {
  const activatable = useActivatable({ onClick, isDisabled });
  const isClickable = Boolean(onClick) && !isDisabled;
  const explicitDotColor =
    typeof dot === 'string' && dot.length > 0 ? dot : undefined;
  const hasDot = dot === true || explicitDotColor !== undefined;
  const tintStyle = useMemo(
    () => (tint ? pillTintStyle(tint) : undefined),
    [tint]
  );

  return (
    <span
      className={cx(
        pillClassName({ size, color, tint, hasDot }),
        isClickable && CHIP_INTERACTIVE_CLASSES,
        onClick && isDisabled && CHIP_DISABLED_CLASSES,
        className
      )}
      style={tintStyle}
      {...activatable}
      aria-label={onClick ? ariaLabel : undefined}
      title={title}
    >
      {hasDot && <PillDot color={color} dotColor={explicitDotColor ?? tint} />}
      {children}
    </span>
  );
}
